import React, { useState, useMemo } from 'react';
import { Calculator, ChevronDown, ChevronUp, TrendingUp, Printer } from 'lucide-react';

interface ScheduleRow {
  month: number;
  date: string;
  principal: number;
  interest: number;
  total: number;
  remaining: number;
  isGrace: boolean;
}

interface Props {
  loanAmount?: number;
  repaymentPeriodYears?: number;
  gracePeriodMonths?: number;
  interestRate?: number; // decimal, e.g. 0.001 for 0.1%
  disbursementDate?: string;
}

function calculateSchedule(
  amount: number,
  periodYears: number,
  graceMonths: number,
  annualRate: number,
  startDate: string,
): ScheduleRow[] {
  if (!amount || !periodYears) return [];

  const monthlyRate = annualRate / 12;
  const totalMonths = periodYears * 12;
  const repaymentMonths = totalMonths - graceMonths;
  if (repaymentMonths <= 0) return [];

  // Annuity payment for repayment period
  let pmt: number;
  if (monthlyRate === 0) {
    pmt = amount / repaymentMonths;
  } else {
    const factor = Math.pow(1 + monthlyRate, repaymentMonths);
    pmt = amount * (monthlyRate * factor) / (factor - 1);
  }

  const rows: ScheduleRow[] = [];
  let remaining = amount;
  const start = startDate ? new Date(startDate) : new Date();

  for (let m = 1; m <= totalMonths; m++) {
    const date = new Date(start);
    date.setMonth(date.getMonth() + m);
    const dateStr = date.toISOString().substring(0, 10);

    const interest = remaining * monthlyRate;

    if (m <= graceMonths) {
      // Grace period — interest only
      rows.push({
        month: m,
        date: dateStr,
        principal: 0,
        interest: Math.round(interest * 100) / 100,
        total: Math.round(interest * 100) / 100,
        remaining: Math.round(remaining * 100) / 100,
        isGrace: true,
      });
    } else {
      const principal = pmt - interest;
      remaining = Math.max(0, remaining - principal);
      rows.push({
        month: m,
        date: dateStr,
        principal: Math.round(principal * 100) / 100,
        interest: Math.round(interest * 100) / 100,
        total: Math.round(pmt * 100) / 100,
        remaining: Math.round(remaining * 100) / 100,
        isGrace: false,
      });
    }
  }

  // Fix last row rounding
  if (rows.length > 0) {
    const last = rows[rows.length - 1];
    if (last.remaining > 0 && last.remaining < 1) {
      last.principal += last.remaining;
      last.total += last.remaining;
      last.remaining = 0;
    }
  }

  return rows;
}

export const LoanCalculatorWidget: React.FC<Props> = ({
  loanAmount = 0,
  repaymentPeriodYears = 7,
  gracePeriodMonths = 0,
  interestRate = 0.001,
  disbursementDate,
}) => {
  const [amount, setAmount] = useState(loanAmount || '');
  const [years, setYears] = useState(repaymentPeriodYears);
  const [grace, setGrace] = useState(gracePeriodMonths);
  const [rate, setRate] = useState((interestRate * 100).toFixed(2)); // display as %
  const [expanded, setExpanded] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);

  const numAmount = Number(amount) || 0;
  const numRate = (Number(rate) || 0) / 100;
  const startDate = disbursementDate || new Date().toISOString().substring(0, 10);

  const schedule = useMemo(
    () => calculateSchedule(numAmount, years, grace, numRate, startDate),
    [numAmount, years, grace, numRate, startDate],
  );

  const monthlyPayment = schedule.find(r => !r.isGrace)?.total || 0;
  const gracePayment = schedule.find(r => r.isGrace)?.total || 0;
  const totalInterest = schedule.reduce((s, r) => s + r.interest, 0);
  const totalRepayment = numAmount + totalInterest;

  const handlePrint = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Harmonogram spłat</title>
      <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:6px 8px;text-align:right}th{background:#f0f0f0}td:first-child,th:first-child{text-align:center}.grace{background:#fffbeb}.summary{margin-top:16px}h2{margin-bottom:4px}</style>
    </head><body>
      <h2>Harmonogram spłat pożyczki</h2>
      <p>Kwota: ${numAmount.toLocaleString('pl-PL')} zł | Okres: ${years} lat | Karencja: ${grace} msc | Oprocentowanie: ${rate}%</p>
      <table><thead><tr><th>#</th><th>Data</th><th>Kapitał</th><th>Odsetki</th><th>Rata</th><th>Saldo</th></tr></thead><tbody>`);
    schedule.forEach(r => {
      w.document.write(`<tr class="${r.isGrace ? 'grace' : ''}"><td>${r.month}</td><td>${r.date}</td><td>${r.principal.toFixed(2)}</td><td>${r.interest.toFixed(2)}</td><td>${r.total.toFixed(2)}</td><td>${r.remaining.toFixed(2)}</td></tr>`);
    });
    w.document.write(`</tbody></table>
      <div class="summary"><strong>Suma odsetek:</strong> ${totalInterest.toFixed(2)} zł | <strong>Łączna spłata:</strong> ${totalRepayment.toFixed(2)} zł</div>
    </body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <div className="bg-gradient-to-br from-sky-50 to-white rounded-lg shadow border border-sky-200">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-sky-50/50 transition-colors"
      >
        <h3 className="font-bold text-sky-800 flex items-center gap-2">
          <Calculator size={18} />
          Kalkulator pożyczki
        </h3>
        {expanded ? <ChevronUp size={16} className="text-sky-400" /> : <ChevronDown size={16} className="text-sky-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Kwota pożyczki (zł)</label>
              <input
                type="number"
                min="0"
                max="175000"
                step="1000"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full border rounded-lg p-2 text-sm font-semibold"
                placeholder="np. 120000"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Okres spłaty (lat)</label>
              <select
                value={years}
                onChange={e => setYears(Number(e.target.value))}
                className="w-full border rounded-lg p-2 text-sm"
                aria-label="Okres spłaty"
              >
                {[1, 2, 3, 4, 5, 6, 7].map(y => (
                  <option key={y} value={y}>{y} {y === 1 ? 'rok' : y < 5 ? 'lata' : 'lat'}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Karencja (msc)</label>
              <select
                value={grace}
                onChange={e => setGrace(Number(e.target.value))}
                className="w-full border rounded-lg p-2 text-sm"
                aria-label="Okres karencji"
              >
                {Array.from({ length: 13 }, (_, i) => (
                  <option key={i} value={i}>{i} msc</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Oprocentowanie (%)</label>
              <input
                type="number"
                min="0"
                max="20"
                step="0.01"
                value={rate}
                onChange={e => setRate(e.target.value)}
                className="w-full border rounded-lg p-2 text-sm"
              />
            </div>
          </div>

          {/* Summary */}
          {numAmount > 0 && schedule.length > 0 && (
            <div className="bg-sky-100 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-sky-700">Rata miesięczna</span>
                <span className="font-bold text-sky-900 text-lg">{monthlyPayment.toFixed(2)} zł</span>
              </div>
              {grace > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-sky-700">Rata w karencji</span>
                  <span className="font-semibold text-amber-700">{gracePayment.toFixed(2)} zł</span>
                </div>
              )}
              <div className="flex justify-between text-xs text-sky-600">
                <span>Suma odsetek</span>
                <span>{totalInterest.toFixed(2)} zł</span>
              </div>
              <div className="flex justify-between text-xs text-sky-600">
                <span>Łączna spłata</span>
                <span className="font-semibold">{totalRepayment.toFixed(2)} zł</span>
              </div>
            </div>
          )}

          {/* Schedule toggle + print */}
          {numAmount > 0 && schedule.length > 0 && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowSchedule(!showSchedule)}
                className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium text-sky-700 hover:bg-sky-50 rounded-lg border border-sky-200 transition-colors"
              >
                <TrendingUp size={14} />
                {showSchedule ? 'Ukryj harmonogram' : 'Pokaż harmonogram'}
              </button>
              <button
                type="button"
                onClick={handlePrint}
                className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded-lg border transition-colors"
              >
                <Printer size={14} />
                Drukuj
              </button>
            </div>
          )}

          {/* Repayment schedule table */}
          {showSchedule && schedule.length > 0 && (
            <div className="max-h-80 overflow-y-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead className="bg-sky-50 sticky top-0">
                  <tr>
                    <th className="text-center px-2 py-1.5 font-semibold text-sky-700">#</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-sky-700">Data</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-sky-700">Kapitał</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-sky-700">Odsetki</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-sky-700">Rata</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-sky-700">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map(r => (
                    <tr key={r.month} className={r.isGrace ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                      <td className="text-center px-2 py-1 text-gray-500">{r.month}</td>
                      <td className="px-2 py-1 font-mono text-gray-700">{r.date}</td>
                      <td className="text-right px-2 py-1">{r.principal.toFixed(2)}</td>
                      <td className="text-right px-2 py-1 text-amber-600">{r.interest.toFixed(2)}</td>
                      <td className="text-right px-2 py-1 font-semibold">{r.total.toFixed(2)}</td>
                      <td className="text-right px-2 py-1 text-gray-500">{r.remaining.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-sky-50 font-semibold">
                  <tr>
                    <td colSpan={2} className="px-2 py-1.5 text-right text-sky-700">Suma:</td>
                    <td className="text-right px-2 py-1.5">{schedule.reduce((s, r) => s + r.principal, 0).toFixed(2)}</td>
                    <td className="text-right px-2 py-1.5 text-amber-600">{totalInterest.toFixed(2)}</td>
                    <td className="text-right px-2 py-1.5">{schedule.reduce((s, r) => s + r.total, 0).toFixed(2)}</td>
                    <td className="text-right px-2 py-1.5">0.00</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
